import React, { useState, useEffect, useMemo } from 'react';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { ErrorButton, PrimaryButton } from "../UI";
import { useUserContext } from '../../contexts/UserContext';
import { toast } from 'react-toastify';
import { deleteSpecificContentfulIntegration } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import AgGridWrapper from '../UI/AgGrid';
import ConfirmModal from '../UI/Modal/confirmModal';

const DeleteIntegrationModal = ({ setDeleteIntegration, id, setActionLoading, getContentfulData }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState('');
    const [showGrid, setShowGrid] = useState(false);
    const [error, setErrors] = useState('');
    const [loading, setLoading] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const modalText = content.CONTENTFUL_CONTENT.delete;

    // Fetch dependencies on modal open
    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const result = await deleteSpecificContentfulIntegration(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    id,
                    true
                );

                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data.data);
                    setMessage(result.data.message);
                }
            } catch (err) {
                setErrors(err?.message || content.CONTENTFUL_CONTENT.error.fetch_failed);
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [id, userData, content]);

    const handleDeleteProduct = async () => {
        try {
            setActionLoading && setActionLoading(true);
            setIsDeleting(true);
            const result = await deleteSpecificContentfulIntegration(
                userData.token,
                userData.selectedOrganization.org_token,
                id
            );
            if (result) {
                await getContentfulData();
                setDeleteIntegration(false);
                toast.success(content.CONTENTFUL_CONTENT.success.delete);
            }
        } catch (err) {
            setErrors(err?.message || content.CONTENTFUL_CONTENT.error.delete_failed);
        } finally {
            setActionLoading && setActionLoading(false);
            setIsDeleting(false);
            setShowConfirm(false);
        }
    };

    // Flatten dependencies for grid preview
    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.experience_offer_mappings) {
            rows.push(
                ...dependencies.experience_offer_mappings.map(d => ({
                    type: 'Experience Mapping',
                    name: d.experience_name,
                    id: d.records.map(r => r.id).join(", "),
                    ...d
                }))
            );
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: 'Type', field: 'type', flex: 1 },
        { headerName: 'Name', field: 'name', flex: 2 },
        { headerName: 'ID', field: 'id', flex: 2 }
    ];

    return (
        <>
            <PrimaryModal title={modalText.title} hideModal={setDeleteIntegration}>
                <div className='p-6 space-y-6'>
                    {loading ? (
                        <p className="text-center text-gray-500">{modalText.checking_dependencies}</p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message} <br />
                                            <span className="font-semibold text-red-500">
                                                {modalText.warning}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {modalText.description}
                                        </p>
                                    )}
                                    {error && <p className="text-red-500">{error}</p>}
                                    <div className="mb-4 flex justify-end space-x-4">
                                        {message ? (
                                            <>
                                                <PrimaryButton handleClick={() => setShowGrid(true)}>
                                                    {modalText.review_dependencies_cta}
                                                </PrimaryButton>
                                                <ErrorButton handleClick={() => setShowConfirm(true)}>
                                                    {modalText.delete_integration_cta}
                                                </ErrorButton>
                                            </>
                                        ) : (
                                            <ErrorButton loading={isDeleting} handleClick={() => handleDeleteProduct()}>
                                                {modalText.formLabels.deactivate_integration_cta}
                                            </ErrorButton>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold">{modalText.dependencies_preview}</p>
                                    <div style={{ height: "300px", overflow : "auto", width:"100%" }}>
                                        <AgGridWrapper
                                            tableId="contentful-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end space-x-4">
                                        <PrimaryButton handleClick={() => setShowGrid(false)}>
                                            {modalText.back}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>

            {showConfirm && (
                <ConfirmModal
                    title={modalText.confirm.title}
                    description={modalText.confirm.description}
                    ConfirmText={modalText.confirm.confirm_text}
                    error={error}
                    loading={isDeleting}
                    hideModal={setShowConfirm}
                    handleConfirmation={(confirmed) => {
                        if (confirmed) handleDeleteProduct();
                        else setShowConfirm(false);
                    }}
                />
            )}
        </>
    );
};

export default DeleteIntegrationModal;